import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { map } from "lodash";
import { ConstructionOutlined } from "@material-ui/icons";
import { staleGuard } from "src/utils/staleGuard";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  startLoadingGetOrders: false,
  createdSuccess: false,
  error: false,
  orders: [],
  order: null,
  onlyOrders: [],
  orders_filters: [],
  ordersOpen: [],
  collapseRowFunctions: [],
  timeLapseSelected: 1,
  isCreatingOrder: false,
  isOrders: [],
  isRight: [],
  isLoadingPartialPayment: false,
};

const slice = createSlice({
  name: "order",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    startLoadingGetOrders(state) {
      state.startLoadingGetOrders = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      state.createdSuccess = false;
    },

    setPaymentPartialPayment(state, action) {
      state.isLoadingPartialPayment = action.payload;
    },

    changeTimeLapseSuccess(state, action) {
      state.timeLapseSelected = action.payload;
    },

    // GET OrderS
    getOrdersSuccess(state, action) {
      state.isLoading = false;
      state.orders = action.payload;
      state.createdSuccess = false;
      state.startLoadingGetOrders = false;
    },
    // GET Order
    getOrderSuccess(state, action) {
      state.isLoading = false;
      state.order = action.payload;
      state.createdSuccess = false;
    },
    // GET Delete Order
    deleteOrderSuccess(state, action) {
      state.isLoading = false;
      state.orders = state.orders.filter((ord) => action.payload != ord.id);
      state.onlyOrders = state.onlyOrders.filter(
        (ord) => action.payload != ord.id
      );
    },
    getOrdersByMesaSuccess(state, action) {
      state.isLoading = false;
      state.orders_filters = state.orders.filter(
        (o) => o.idMesa === action.payload.idMesa
      );
    },
    acceptOrderSuccess(state, action) {
      state.isLoading = false;
      state.onlyOrders = map(state.onlyOrders, (_order) => {
        if (_order.id === action.payload) {
          return {
            ..._order,
            declined: false,
            approved: true,
          };
        } else return _order;
      });
    },
    getOnlyOrdersSuccess(state, action) {
      state.onlyOrders = action.payload;
      state.createdSuccess = false;
      state.startLoadingGetOrders = false;
    },
    declineOrderSuccess(state, action) {
      state.isLoading = false;
      state.onlyOrders = state.onlyOrders.map((_order) => {
        if (_order.id === action.payload) {
          return {
            ..._order,
            approved: false,
            declined: true,
          };
        } else return _order;
      });
    },
    createOrderSuccess(state, action) {
      state.isLoading = false;
      state.createdSuccess = true;
      state.order = action.payload;
    },
    cleanSuccess(state, action) {
      state.isLoading = false;
      state.order = null;
      state.orders = [];
      state.orders_filters = [];
      state.ordersOpen = [];
      state.collapseRowFunctions = [];
      state.timeLapseSelected = 1;
    },
    // Resta unidades de productos de los orders cargados — optimistic
    // tras "Mover productos a otra mesa". Sin esto los productos vuelven
    // a aparecer en la vista hasta que el SignalR / refetch confirma el
    // movimiento (race visual).
    //
    // Payload: `[{ pedidoId, counterProductId, unidades }]` — los mismos
    // datos que se mandaron al endpoint `ChangeMesaProducts`.
    optimisticRemoveProducts(state, action) {
      const moves = action.payload || [];
      if (moves.length === 0) return;
      for (const ord of state.orders || []) {
        const matches = moves.filter((m) => m.pedidoId === ord.id);
        if (matches.length === 0) continue;
        ord.productos = (ord.productos || [])
          .map((prod) => {
            const move = matches.find(
              (m) => m.counterProductId === prod.idNumPedido,
            );
            if (!move) return prod;
            const remaining = Math.max(0, (prod.unidades || 0) - (move.unidades || 0));
            if (remaining === 0) return null;
            return { ...prod, unidades: remaining };
          })
          .filter(Boolean);
      }
    },
    // 2026-09-03 · Split-by-comensal. Muta `idComensal` de las líneas
    // afectadas por `moverProductosComensalBackend` en el store `orders`
    // — sin esto, `pendingProducts` del PaymentDialog no refleja el
    // cambio hasta refetch/refresh. Match por (pedidoId,
    // counterProductId, complementoId).
    // Payload: { entries: [{pedidoID, counterProductID, complementoID}],
    //   newComensal }.
    optimisticSetComensal(state, action) {
      const { entries, newComensal } = action.payload || {};
      if (!Array.isArray(entries) || entries.length === 0) return;
      const n = Number(newComensal) || 0;
      for (const ord of state.orders || []) {
        const matches = entries.filter((e) => e.pedidoID === ord.id);
        if (matches.length === 0) continue;
        ord.productos = (ord.productos || []).map((prod) => {
          const match = matches.find(
            (e) =>
              (e.counterProductID ?? -1) === (prod.idNumPedido ?? prod.counterProductId ?? -1)
              && (e.complementoID ?? -1) === (prod.idComplemento ?? -1),
          );
          if (!match) return prod;
          return { ...prod, idComensal: n };
        });
      }
    },
    addCollapseFunction(state, action) {
      if (
        state.collapseRowFunctions.find((f) => f.id === action.payload.id) ==
        null
      )
        state.collapseRowFunctions.push(action.payload);
    },
    collapseAllExcepting(state, action) {
      state.collapseRowFunctions = state.collapseRowFunctions.map((crf) => {
        if (crf.id == action.payload) return { ...crf, open: !crf.open };
        else return { ...crf, open: false };
      });
    },
    addPedido(state, action) {
      state.onlyOrders = [action.payload, ...state.onlyOrders];

      // remove duplicated orders
      let uniqueOrders = state.onlyOrders.filter(
        (v, i, a) => a.findIndex((t) => t.id === v.id) === i
      );
      state.onlyOrders = uniqueOrders;
    },
    updatePayOrders(state, action) {
      var isRight = action.payload;

      state.orders =
        // Encontramos el pedido de orders y filtramos sus productos
        map(state.orders, (ord) => {
          var orderPay = isRight.find((ordPay) => ordPay.PedidoID === ord.id);

          if (orderPay === undefined) {
            return ord;
          } else {
            // 2026-09-08 · Split-by-comensal. El match antiguo solo por
            // `CounterProductId` marcaba TODOS los buckets del mismo
            // counter como pagados — pero con post-split N sub-líneas
            // por comensal comparten CounterProductID (LineIndex las
            // separa; el DTO agrupa por {CounterProductID, IdComensal}).
            // Resultado: pagabas al comensal 3 y desaparecían los 4.
            //
            // Fix: pool consumible de pay-entries, match por
            // (CounterProductId, IdComensal), y consumir `unidades` de
            // cada pay hasta agotarla — así 2 buckets del mismo counter
            // con distinto comensal se pagan independientemente.
            const payPool = orderPay.Productos.map((p) => ({
              CounterProductId: p.CounterProductId,
              IdComensal: p.IdComensal ?? null,
              remaining: Number(p.unidades) || 0,
            }));

            var prods = ord.productos.map((prod) => {
              const prodComensal = Number(prod.idComensal) || 0;
              // Preferir match con IdComensal (post-split); si el
              // payload no envía IdComensal, caer al match legacy solo
              // por counter para no romper cobros pre-split-by-comensal.
              let idx = payPool.findIndex((pp) =>
                pp.remaining > 0 &&
                prod.idNumPedido === pp.CounterProductId &&
                pp.IdComensal != null &&
                Number(pp.IdComensal) === prodComensal
              );
              if (idx === -1) {
                idx = payPool.findIndex((pp) =>
                  pp.remaining > 0 &&
                  prod.idNumPedido === pp.CounterProductId &&
                  pp.IdComensal == null
                );
              }
              if (idx === -1) return prod;

              const pp = payPool[idx];
              const libre = Math.max(
                0,
                Number(prod.unidades || 0) - Number(prod.unidadesPagadas || 0),
              );
              const take = Math.min(libre, pp.remaining);
              pp.remaining -= take;

              const newUnidadesPagadas =
                Number(prod.unidadesPagadas || 0) + take;
              const isFullyPaid =
                newUnidadesPagadas >= Number(prod.unidades || 0);
              return {
                ...prod,
                unidadesPagadas: newUnidadesPagadas,
                pagado: isFullyPaid,
              };
            });

            // Chequear si todos los pedidos están pagados, para poner el order a pagado
            var allPay = prods.every((prod) => prod.pagado);

            return {
              ...ord,
              pagado: allPay,
              productos: prods,
            };
          }
        });
    },

    updateAllOrdersPaid(state, action) {
      var id = action.payload;
      state.orders =
        // Ponemos todos los orders a pagado
        map(state.orders, (ord) => {
          if (id == ord.idMesa) {
            var prods = ord.productos.map((prod) => {
              prod.pagado = true;
              prod.unidadesPagadas = prod.unidades;
              prod.precioPagado = prod.precio * prod.unidades;

              return prod;
            });

            return {
              ...ord,
              pagado: true,
              productos: prods,
            };
          } else {
            return { ...ord };
          }
        });
    },
    isReadyOrderSuccess(state, action) {
      const { id, products } = action.payload;

      state.onlyOrders = map(state.onlyOrders, (ord) => {
        if (id === ord.id) {
          return {
            ...ord,
            productos: ord?.productos.map((prod) => {
              if (products?.length === 0) {
                return {
                  ...prod,
                  isReady: true,
                };
              } else {
                if (products.includes(prod.id)) {
                  return {
                    ...prod,
                    isReady: true,
                  };
                } else {
                  return prod;
                }
              }
            }),
          };
        } else {
          return ord;
        }
      });
    },
    handleRequestingOrder(state, action) {
      state.isCreatingOrder = action.payload;
    },
    updateOrderPayment(state, action) {
      let auxData = action.payload;
      let data = [];

      if (auxData.length > 0) {
        auxData = auxData.sort((a, b) => b.id - a.id);

        auxData.forEach((element) => {
          if (data.length === 0) {
            data.push(element);
            return;
          }

          let index = data.findIndex((d) => {
            if (d.id !== element.id) return false;

            if (d.notas !== element.notas) return false;

            if (d.precio !== element.precio) return false;

            if (d.idNumPedido !== element.idNumPedido) return false;

            if (d.idOrder !== element.idOrder) return false;

            if (d.complementos.length !== element.complementos.length)
              return false;

            d.complementos.forEach((complement) => {
              var index = element.complementos.findIndex((complement2) => {
                if (complement.id !== complement2.id) return false;

                if (complement.precio !== complement2.precio) return false;

                if (complement.unidades !== complement2.unidades) return false;

                if (complement.quantity !== complement2.quantity) return false;

                return true;
              });

              if (index === -1) return false;

              return true;
            });

            return true;
          });

          if (index === -1) {
            data.push(element);
            return;
          }

          data = data.map((d, i) => {
            if (i === index)
              return {
                ...d,
                unidades: d.unidades + element.unidades,
              };

            return d;
          });
        });

        auxData = data;
      }

      state.isOrders = auxData;
    },

    updateIsRightPayment(state, action) {
      let auxData = action.payload;
      let data = [];

      if (auxData.length > 0) {
        auxData = auxData.sort((a, b) => b.id - a.id);

        auxData.forEach((element) => {
          if (data.length === 0) {
            data.push(element);
            return;
          }

          let index = data.findIndex((d) => {
            if (d.id !== element.id) return false;

            if (d.notas !== element.notas) return false;

            if (d.precio !== element.precio) return false;

            if (d.idNumPedido !== element.idNumPedido) return false;

            if (d.idOrder !== element.idOrder) return false;

            if (d.complementos.length !== element.complementos.length)
              return false;

            d.complementos.forEach((complement) => {
              var index = element.complementos.findIndex((complement2) => {
                if (complement.id !== complement2.id) return false;

                if (complement.precio !== complement2.precio) return false;

                if (complement.unidades !== complement2.unidades) return false;

                if (complement.quantity !== complement2.quantity) return false;

                return true;
              });

              if (index === -1) return false;

              return true;
            });

            return true;
          });

          if (index === -1) {
            data.push(element);
            return;
          }

          data = data.map((d, i) => {
            if (i === index)
              return {
                ...d,
                unidades: d.unidades + element.unidades,
              };

            return d;
          });
        });

        auxData = data;
      }

      state.isRight = auxData;
    },

    resetIsRightPayment(state) {
      state.isRight = [];
    },

    // 2026-09-07 · Mutación optimista tras marcarMenuParcial. Payload:
    // { idPedido, counterProductId, isPartial }. Actualiza el flag en
    // el order + producto correspondiente para que el chip "incompleto"
    // aparezca/desaparezca al instante sin esperar al SignalR SyncCart.
    setProductoPartialFlag(state, action) {
      const { idPedido, counterProductId, isPartial } = action.payload || {};
      if (idPedido == null || counterProductId == null) return;
      const flag = !!isPartial;
      const applyToList = (list) => {
        if (!Array.isArray(list)) return list;
        return list.map((o) => {
          if (o?.id !== idPedido) return o;
          const productos = Array.isArray(o.productos) ? o.productos : [];
          return {
            ...o,
            productos: productos.map((p) => {
              const cid = p?.idNumPedido ?? p?.counterProductId;
              if (Number(cid) !== Number(counterProductId)) return p;
              return { ...p, isPartial: flag };
            }),
          };
        });
      };
      state.orders = applyToList(state.orders);
      state.onlyOrders = applyToList(state.onlyOrders);
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getOrderSuccess,
  getOrdersSuccess,
  createOrderSuccess,
  acceptOrderSuccess,
  declineOrderSuccess,
  getOrdersByMesaSuccess,
  cleanSuccess,
  optimisticRemoveProducts,
  optimisticSetComensal,
  addCollapseFunction,
  startLoadingGetOrders,
  collapseAllExcepting,
  addPedido,
  changeTimeLapseSuccess,
  updatePayOrders,
  deleteOrderSuccess,
  handleRequestingOrder,
  updateAllOrdersPaid,
  updateOrderPayment,
  updateIsRightPayment,
  resetIsRightPayment,
  setPaymentPartialPayment,
  isReadyOrderSuccess,
  setProductoPartialFlag,
} = slice.actions;

// ----------------------------------------------------------------------

export function getOnlyOrders(config, { force = false } = {}) {
  return async (dispatch) => {
    // El listado de pedidos activos (last24h u OnlyOrders) es pesado
    // (172 KB en el HAR) y se llamaba en cada remount del TPV. SignalR
    // (PedidoHub) empuja los cambios individuales; con staleGuard
    // evitamos el POST redundante durante la ventana. Clave incluye el
    // config serializado — llamadas con filtros distintos NO comparten
    // cache (crítico: OnlyOrders/false y OnlyOrders/true dan sets
    // distintos).
    const key = `orders/getOnlyOrders/${JSON.stringify(config || {})}`;
    if (!force && staleGuard(key)) return;
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.startLoadingGetOrders());
    try {
      const response = await axios.post(
        `pedidos/getOrders`,
        JSON.stringify({ ...config }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.getOnlyOrdersSuccess(response.data.orders));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getOrders(config) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.startLoadingGetOrders());
    try {
      const response = await axios.post(
        `pedidos/getOrders`,
        JSON.stringify({ ...config }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.getOrdersSuccess(response.data.orders));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getOrder(name) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get("orders/order", {
        params: { name },
      });
      dispatch(slice.actions.getOrderSuccess(response.data.order));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error.response);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getOrdersByMesa(idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const user = window.localStorage.getItem("userId");
    try {
      const response = await axios.get(`pedidos/${user}/${idMesa}`);
      dispatch(slice.actions.getOrdersSuccess(response.data.orders));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getOrdersBySession(idSession, idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const user = window.localStorage.getItem("userId");
    try {
      const response = await axios.get(
        `pedidos/${user}/${idMesa}/${idSession}`
      );
      dispatch(slice.actions.getOrdersSuccess(response.data.orders));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createOrder(order) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const comensal = window.localStorage.getItem("comensalId");
      const response = await axios.post(
        "pedidos",
        JSON.stringify({
          precio: order.precio,
          idMesa: order.idMesa,
          idRestaurante: order.idRestaurante,
          productos: order.productos,
          idSession: order.idSession,
          idComensal: comensal ? comensal : null,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.createOrderSuccess(response.data.order));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function cleanOrder() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.cleanSuccess());
  };
}

export function acceptOrder(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.acceptOrderSuccess(id));
      await axios.get(`pedidos/accept/${id}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createOrderAcceptPaymentFranquicica(order) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(
        "pedidos/add/accept/payment",
        JSON.stringify({ ...order }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.createOrderSuccess(response.data.order));
      dispatch(slice.actions.getOrderSuccess(response.data.order));
      dispatch(slice.actions.acceptOrderSuccess(response.data.order.id));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createOrderAndAcceptIt(order) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const comensal = window.localStorage.getItem("comensalId");
      const response = await axios.post(
        "pedidos/add/accept",
        JSON.stringify({
          precio: order.precio,
          idMesa: order.idMesa,
          idRestaurante: order.idRestaurante,
          productos: order.productos,
          idSession: order.idSession,
          idComensal: comensal ? comensal : null,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.createOrderSuccess(response.data.order));
      dispatch(slice.actions.getOrderSuccess(response.data.order));
      dispatch(slice.actions.acceptOrderSuccess(response.data.order.id));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createOrderAndAcceptItDelete(order) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const comensal = window.localStorage.getItem("comensalId");
      const response = await axios.post(
        "pedidos/add/accept/delete",
        JSON.stringify({
          precio: order.precio,
          idMesa: order.idMesa,
          idRestaurante: order.idRestaurante,
          productos: order.productos,
          idSession: order.idSession,
          idComensal: comensal ? comensal : null,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      dispatch(slice.actions.createOrderSuccess(response.data.order));
      dispatch(slice.actions.acceptOrderSuccess(response.data.order.id));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function declineOrder(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.declineOrderSuccess(id));
      await axios.get(`pedidos/decline/${id}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function isReadyOrder(id, idSession, products) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(
        slice.actions.isReadyOrderSuccess({
          id,
          products,
        })
      );
      await axios.post(
        `pedidos/readyPedidoProduct/${id}/${idSession}`,
        products,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createOrderWithProducts(orders, idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.updatePayOrders(orders));
      const response = await axios.post(
        `pedidos/pagar/producto/${idMesa}`,
        JSON.stringify({
          pedidos: orders,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      //dispatch(slice.actions.createOrderSuccess(response.data.order));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function allOrdersPay(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.updateAllOrdersPaid(id));
      await axios.get(`pedidos/pagar/all/${id}`);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateOrder(order) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.put("pedidos/update", JSON.stringify({ ...order }), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteOrder(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`pedidos/delete/${id}`);
      dispatch(slice.actions.deleteOrderSuccess(id));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ── Menús: ampliación / parcial / status ─────────────────────────
// Cliente que aún no sabe qué segundo plato quiere ahora → el camarero
// comanda el menú como parcial (bloquea cobro) y luego amplía cuando el
// cliente decida. El backend agrupa por CounterProductID (mismo menú
// lógico) y las lines nuevas llevan IsAmpliacion=true para pintarse
// como "AMPLIACIÓN DE MENÚ" en la comanda y con badge en KDS.

export async function getMenuStatus(idPedido, counterProductId) {
  const res = await axios.get(
    `pedidos/menu/status/${idPedido}/${counterProductId}`,
  );
  return res.data;
}

export async function ampliarMenu(idPedido, counterProductId, payload) {
  // Normalizamos el shape que espera el backend DtoAmpliarMenuRequest:
  //   { Nuevos, Eliminados, NotaGlobalChanged, NotaGlobal }
  // Guarda defensiva: garantizamos que Nuevos y Eliminados sean SIEMPRE
  // arrays (no objetos, no undefined) — evita el 400 de deserialización.
  //
  // 2026-09-11 · NotaGlobalChanged permite editar solo la nota del
  // producto sin tocar complementos (antes salía "no hay cambios").
  const body = {
    Nuevos: Array.isArray(payload?.Nuevos) ? payload.Nuevos : [],
    Eliminados: Array.isArray(payload?.Eliminados) ? payload.Eliminados : [],
    NotaGlobalChanged: !!payload?.NotaGlobalChanged,
    NotaGlobal: payload?.NotaGlobal ?? null,
  };
  const res = await axios.post(
    `pedidos/menu/ampliar/${idPedido}/${counterProductId}`,
    body,
  );
  return res.data;
}

// 2026-09-07 · Convertido a thunk para aplicar mutación optimista local:
// el chip "incompleto" del cart + orders del dispositivo actual se
// actualiza al instante sin esperar a que llegue el SignalR ChangeMesa
// que refresca el cart entero (~200-400ms extra en red 4G). Además la
// firma sigue devolviendo la promesa del axios para que los callers
// puedan hacer await como antes.
export function marcarMenuParcial(idPedido, counterProductId, isPartial = true) {
  return async (dispatch) => {
    const flag = !!isPartial;
    // Mutación optimista ANTES del await — el UI del dispositivo actual
    // refleja el cambio al instante. Si el backend falla revertiremos
    // en el catch.
    dispatch(slice.actions.setProductoPartialFlag({
      idPedido,
      counterProductId,
      isPartial: flag,
    }));
    // El slice product tiene su propio checkout.cart que también hay
    // que sincronizar. Import dinámico para evitar ciclo entre slices.
    try {
      const { setCartLinePartialFlag } = await import("src/redux/slices/product");
      dispatch(setCartLinePartialFlag({
        idPedido,
        counterProductId,
        isPartial: flag,
      }));
    } catch (_) { /* best-effort */ }

    try {
      const res = await axios.post(
        `pedidos/menu/parcial/${idPedido}/${counterProductId}?isPartial=${flag}`,
      );
      return res.data;
    } catch (err) {
      // Revertir mutación optimista si el backend rechazó el cambio.
      dispatch(slice.actions.setProductoPartialFlag({
        idPedido,
        counterProductId,
        isPartial: !flag,
      }));
      try {
        const { setCartLinePartialFlag } = await import("src/redux/slices/product");
        dispatch(setCartLinePartialFlag({
          idPedido,
          counterProductId,
          isPartial: !flag,
        }));
      } catch (_) { /* best-effort */ }
      throw err;
    }
  };
}

export async function getMenusPartial(idSession) {
  const res = await axios.get(`pedidos/menu/partial/${idSession}`);
  return res.data;
}
